import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";

import ClearIcon from "@mui/icons-material/Close";

import { get } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosFilterBar, IosList, IosListItem, IosPage, IosSectionHeader, iosColors } from "mobile/components/IosParity";

const filterOptions = [
  { key: "open", label: "Open", icon: "pending_actions", accent: "orange" },
  { key: "completed", label: "Done", icon: "verified", accent: "green" },
  { key: "all", label: "All", icon: "list_alt", accent: "orange" },
];

const decimalText = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};
export default function PullOrdersMobile() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("open");
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setSubmittedSearch(search.trim()), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["m-pull-queue", status, submittedSearch],
    queryFn: () => get("/api/sales-orders/staff/pull-queue", { status, q: submittedSearch, pageSize: 120 }),
    staleTime: 5_000,
  });

  const rows = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data]);

  return (
    <IosPage>
      <MobileTopBar title="Order Pulls" subtitle={isFetching ? "Syncing..." : "Warehouse"} onBack={() => navigate("/m/dashboard")} />
      <Stack spacing={1.05} sx={{ mt: 1 }}>
        <IosSectionHeader eyebrow="Warehouse" title="Pull requests" subtitle="Verify tags and quantities before shipping." />
        <IosFilterBar label="Queue" value={status} options={filterOptions} onChange={setStatus} />
        <TextField
          fullWidth
          placeholder="Search order, pull, or customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setSubmittedSearch(search.trim());
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small">search</Icon>
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {isLoading ? <LinearProgress /> : null}
        {error ? (
          <Typography sx={{ color: iosColors.red, fontSize: 13, fontWeight: 850 }}>
            Unable to load pull requests.
          </Typography>
        ) : null}

        <IosList>
          {rows.map((order) => {
            const pull = order.pullOrder || {};
            const lines = Array.isArray(order.lines) ? order.lines : [];
            const targetId = pull.documentId || pull.id || order.documentId || order.id;
            return (
              <IosListItem
                key={targetId}
                icon="inventory_2"
                title={pull.pullOrderNumber || order.salesOrderNumber || "Pull request"}
                meta={[order.salesOrderNumber, order.customer?.name].filter(Boolean).join(" • ") || "Warehouse pull"}
                trailing={`${lines.length} lines`}
                accent={String(pull.status || "").toLowerCase() === "completed" ? "green" : "orange"}
                onClick={() => navigate(`/m/pull-orders/${targetId}`)}
              />
            );
          })}
        </IosList>

        {!rows.length && !isLoading ? (
          <Box sx={{ px: 0.5 }}>
            <Chip
              size="small"
              icon={<Icon fontSize="small">check_circle</Icon>}
              label={status === "open" ? "No open pull requests" : "No pull requests found"}
              sx={{ bgcolor: "#fff", border: `1px solid ${iosColors.line}`, fontWeight: 850 }}
            />
          </Box>
        ) : null}
        {rows.length ? (
          <Typography sx={{ color: iosColors.muted, fontSize: 12, px: 0.4 }}>
            {rows.length} requests • {decimalText(rows.reduce((sum, order) => sum + (order.lines || []).reduce((s, line) => s + (Number(line.quantity) || 0), 0), 0))} units
          </Typography>
        ) : null}
      </Stack>
    </IosPage>
  );
}
